import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import MainNavigation from '../../components/ui/MainNavigation';
import Icon from '../../components/AppIcon';
import { disputeService } from '../../services/disputeService';
import { supabase } from '../../lib/supabase';

import DisputeOverviewCard from './components/DisputeOverviewCard';
import ResolutionDecisionPanel from './components/ResolutionDecisionPanel';
import EvidenceComparisonTool from './components/EvidenceComparisonTool';
import CommunicationThread from './components/CommunicationThread';

const OwnerDisputeReview = () => {
  const { user, userProfile, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const [disputes, setDisputes] = useState([]);
  const [selectedDispute, setSelectedDispute] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [activeView, setActiveView] = useState('overview');
  const [isLoadingDisputes, setIsLoadingDisputes] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/login');
    }
  }, [loading, isAuthenticated, navigate]);

  useEffect(() => {
    const fetchDisputes = async () => {
      if (!user?.id) return;

      try {
        setIsLoadingDisputes(true);
        setError(null);
        const data = await disputeService?.getOwnerDisputes();
        setDisputes(data || []);
      } catch (err) {
        console.error('Error fetching disputes:', err);
        setError(err?.message || 'Failed to fetch disputes');
      } finally {
        setIsLoadingDisputes(false);
      }
    };

    if (isAuthenticated) {
      fetchDisputes();
    }
  }, [user, isAuthenticated]);

  // Real-time subscription for dispute updates
  useEffect(() => {
    if (!user?.id) return;

    const channel = disputeService?.subscribeToDisputes(user?.id, async (payload) => {
      // Refresh disputes when updates occur
      if (payload?.eventType === 'INSERT' || payload?.eventType === 'UPDATE') {
        try {
          const data = await disputeService?.getOwnerDisputes();
          setDisputes(data || []);
        } catch (err) {
          console.error('Error refreshing disputes:', err);
        }
      }
    });

    return () => {
      supabase?.removeChannel(channel);
    };
  }, [user?.id]);

  const handleDisputeSelect = (dispute) => {
    setSelectedDispute(dispute);
    setActiveView('overview');
  };

  const handleBackToList = () => {
    setSelectedDispute(null);
    setActiveView('overview');
  };

  const handleResolutionSubmit = async (resolutionData) => {
    try {
      // Handle resolution submission logic
      await disputeService?.submitResolution(selectedDispute?.id, resolutionData);
      // Refresh disputes after submission
      const data = await disputeService?.getOwnerDisputes();
      setDisputes(data || []);
      handleBackToList();
    } catch (err) {
      console.error('Error submitting resolution:', err);
      setError(err?.message || 'Failed to submit resolution');
    }
  };

  const getStatusConfig = (status) => {
    switch (status) {
      case 'awaiting_owner_response':
        return { label: 'Awaiting Response', color: 'bg-warning text-warning-foreground', icon: 'Clock' };
      case 'under_review':
        return { label: 'Under Review', color: 'bg-primary text-primary-foreground', icon: 'Search' };
      case 'resolved':
        return { label: 'Resolved', color: 'bg-success text-success-foreground', icon: 'CheckCircle' };
      case 'escalated':
        return { label: 'Escalated', color: 'bg-error text-error-foreground', icon: 'AlertTriangle' };
      default:
        return { label: status, color: 'bg-muted text-muted-foreground', icon: 'Circle' };
    }
  };

  const filteredDisputes = filterStatus === 'all'
    ? disputes
    : disputes?.filter(d => {
        // Map charge status to dispute status
        if (filterStatus === 'awaiting_owner_response') {
          return d?.responses?.length === 0;
        } else if (filterStatus === 'under_review') {
          return d?.responses?.length > 0;
        }
        return true;
      });

  if (loading || isLoadingDisputes) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="font-body text-muted-foreground">Loading disputes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <MainNavigation isAuthenticated={isAuthenticated} user={user} />
      
      <main className="flex-1 pt-24 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center space-x-3 mb-2">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name="Scale" size={24} className="text-primary" strokeWidth={2} />
              </div>
              <div>
                <h1 className="font-heading text-3xl font-bold text-foreground">Dispute Review Center</h1>
                <p className="font-body text-muted-foreground">Review and respond to renter disputes</p>
              </div>
            </div>
          </div>

          {error && (
            <div className="mb-6 bg-error/10 border border-error rounded-lg p-4 flex items-start space-x-3">
              <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0 mt-0.5" strokeWidth={2} />
              <div>
                <p className="font-body font-semibold text-error mb-1">Error Loading Disputes</p>
                <p className="font-body text-sm text-foreground">{error}</p>
              </div>
            </div>
          )}

          {!selectedDispute ? (
            <>
              {/* Filter Tabs */}
              <div className="mb-6 flex flex-wrap gap-2">
                {[
                  { value: 'all', label: 'All Disputes', count: disputes?.length },
                  { value: 'awaiting_owner_response', label: 'Awaiting Response', count: disputes?.filter(d => d?.responses?.length === 0)?.length },
                  { value: 'under_review', label: 'Under Review', count: disputes?.filter(d => d?.responses?.length > 0)?.length }
                ]?.map((filter) => (
                  <button
                    key={filter?.value}
                    onClick={() => setFilterStatus(filter?.value)}
                    className={`px-4 py-2 rounded-lg font-body font-medium transition-smooth ${
                      filterStatus === filter?.value
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground hover:bg-muted/80'
                    }`}
                  >
                    {filter?.label} ({filter?.count})
                  </button>
                ))}
              </div>

              {/* Disputes List */}
              {filteredDisputes?.length === 0 ? (
                <div className="bg-card rounded-lg border border-border p-12 text-center">
                  <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name="Inbox" size={32} className="text-muted-foreground" strokeWidth={2} />
                  </div>
                  <h3 className="font-heading text-xl font-bold text-foreground mb-2">No Disputes Found</h3>
                  <p className="font-body text-muted-foreground">
                    {filterStatus === 'all' ?'You have no active disputes at this time.'
                      : `No disputes in "${filterStatus?.replace(/_/g, ' ')}" status.`}
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-6">
                  {filteredDisputes?.map((dispute) => {
                    const statusConfig = getStatusConfig(
                      dispute?.responses?.length === 0 ? 'awaiting_owner_response' : 'under_review'
                    );
                    
                    return (
                      <div
                        key={dispute?.id}
                        className="bg-card rounded-lg border border-border p-6 hover:shadow-lg transition-smooth cursor-pointer"
                        onClick={() => handleDisputeSelect(dispute)}
                      >
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <h3 className="font-heading text-xl font-bold text-foreground">
                                Dispute on ${dispute?.chargeAmount} Charge
                              </h3>
                              <div className={`flex items-center space-x-1 px-3 py-1 rounded-full ${statusConfig?.color}`}>
                                <Icon name={statusConfig?.icon} size={14} strokeWidth={2} />
                                <span className="font-caption text-xs font-semibold">{statusConfig?.label}</span>
                              </div>
                            </div>
                            <p className="font-caption text-sm text-muted-foreground">
                              Charge Reference: {dispute?.chargeReference} • Filed {new Date(dispute?.disputeDate)?.toLocaleDateString()}
                            </p>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                          <div>
                            <p className="font-caption text-xs text-muted-foreground mb-1">Renter</p>
                            <p className="font-body font-semibold text-foreground">{dispute?.renter?.fullName}</p>
                          </div>
                          <div>
                            <p className="font-caption text-xs text-muted-foreground mb-1">Booking</p>
                            <p className="font-body font-semibold text-foreground">{dispute?.booking?.bookingReference}</p>
                          </div>
                          <div>
                            <p className="font-caption text-xs text-muted-foreground mb-1">Responses</p>
                            <p className="font-body font-semibold text-foreground">{dispute?.responses?.length}</p>
                          </div>
                        </div>

                        <div className="bg-muted/30 rounded-lg p-3">
                          <p className="font-body text-sm text-foreground line-clamp-2">
                            {dispute?.disputeReason || 'No dispute reason provided'}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          ) : (
            <>
              {/* Back Button */}
              <button
                onClick={handleBackToList}
                className="flex items-center space-x-2 text-muted-foreground hover:text-foreground transition-smooth mb-6"
              >
                <Icon name="ArrowLeft" size={20} strokeWidth={2} />
                <span className="font-body font-medium">Back to Disputes</span>
              </button>

              {/* View Tabs */}
              <div className="mb-6 flex flex-wrap gap-2">
                {[
                  { value: 'overview', label: 'Overview', icon: 'FileText' },
                  { value: 'evidence', label: 'Evidence Comparison', icon: 'Image' },
                  { value: 'communication', label: 'Communication', icon: 'MessageSquare' },
                  { value: 'decision', label: 'Resolution', icon: 'CheckCircle' }
                ]?.map((view) => (
                  <button
                    key={view?.value}
                    onClick={() => setActiveView(view?.value)}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-body font-medium transition-smooth ${
                      activeView === view?.value
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground hover:bg-muted/80'
                    }`}
                  >
                    <Icon name={view?.icon} size={18} strokeWidth={2} />
                    <span>{view?.label}</span>
                  </button>
                ))}
              </div>

              {/* Active View Content */}
              {activeView === 'overview' && (
                <DisputeOverviewCard
                  dispute={{
                    ...selectedDispute,
                    caseNumber: selectedDispute?.chargeReference,
                    title: `Dispute on $${selectedDispute?.chargeAmount} Charge`,
                    status: selectedDispute?.responses?.length === 0 ? 'awaiting_owner_response' : 'under_review',
                    createdDate: new Date(selectedDispute?.disputeDate)?.toLocaleDateString(),
                    lastUpdate: new Date(selectedDispute?.updatedAt)?.toLocaleDateString(),
                    responseDeadline: new Date(new Date(selectedDispute?.disputeDate).getTime() + 5 * 24 * 60 * 60 * 1000)?.toLocaleDateString(),
                    trailerName: 'Trailer',
                    trailerImage: '/assets/images/no_image.png',
                    trailerImageAlt: 'Trailer image',
                    renterName: selectedDispute?.renter?.fullName,
                    renterEmail: selectedDispute?.renter?.email,
                    renterRating: 4.5,
                    contestedAmount: selectedDispute?.chargeAmount,
                    originalCharge: selectedDispute?.chargeAmount,
                    description: selectedDispute?.disputeReason,
                    renterEvidence: selectedDispute?.documentationUrls?.map((url, idx) => ({
                      id: idx + 1,
                      type: 'image',
                      url,
                      name: `evidence-${idx + 1}.jpg`,
                      alt: `Evidence ${idx + 1}`,
                      timestamp: new Date(selectedDispute?.disputeDate)?.toLocaleString()
                    })) || [],
                    timeline: [
                      {
                        date: selectedDispute?.createdAt,
                        action: 'Charge Created',
                        actor: 'You',
                        description: `Charged $${selectedDispute?.chargeAmount} for ${selectedDispute?.violationReason}`
                      },
                      {
                        date: selectedDispute?.disputeDate,
                        action: 'Dispute Filed',
                        actor: selectedDispute?.renter?.fullName,
                        description: 'Renter disputed the charge'
                      },
                      ...selectedDispute?.responses?.map(r => ({
                        date: r?.createdAt,
                        action: 'Response Added',
                        actor: r?.responder?.fullName,
                        description: r?.responseMessage
                      })) || []
                    ],
                    messages: selectedDispute?.responses?.map(r => ({
                      id: r?.id,
                      sender: r?.responder?.fullName,
                      role: r?.responderRole,
                      message: r?.responseMessage,
                      timestamp: r?.createdAt,
                      evidence: r?.counterEvidenceUrls?.map((url, idx) => ({
                        id: idx + 1,
                        type: 'image',
                        url,
                        name: `counter-evidence-${idx + 1}.jpg`,
                        alt: `Counter evidence ${idx + 1}`
                      })) || []
                    })) || []
                  }}
                  statusConfig={getStatusConfig(
                    selectedDispute?.responses?.length === 0 ? 'awaiting_owner_response' : 'under_review'
                  )}
                />
              )}
              {activeView === 'evidence' && <EvidenceComparisonTool dispute={selectedDispute} />}
              {activeView === 'communication' && (
                <CommunicationThread
                  dispute={{
                    ...selectedDispute,
                    messages: selectedDispute?.responses?.map(r => ({
                      id: r?.id,
                      sender: r?.responder?.fullName,
                      role: r?.responderRole,
                      message: r?.responseMessage,
                      timestamp: r?.createdAt,
                      evidence: r?.counterEvidenceUrls?.map((url, idx) => ({
                        id: idx + 1,
                        type: 'image',
                        url,
                        name: `counter-evidence-${idx + 1}.jpg`,
                        alt: `Counter evidence ${idx + 1}`
                      })) || []
                    })) || []
                  }}
                />
              )}
              {activeView === 'decision' && <ResolutionDecisionPanel dispute={selectedDispute} onSubmit={handleResolutionSubmit} />}
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default OwnerDisputeReview;